import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Divider,
    Stack,
    Avatar,
    Button,
    Container,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { 
    LocalShipping,
    Build,
    NoCrash,
    Cancel,
    BusinessCenter,
    LocationOn,
    NavigateNext,
    Schedule,
    TrendingUp,
    Security,
    Timeline,
    Group,
    Assignment,
    Event,
    CheckCircle
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import apiClient from '../api';
import { useAuth } from '../context/AuthContext';
import Joyride from 'react-joyride';
import MapComponent from '../components/MapComponent';
import DriverSpotlight from '../components/dashboard/DriverSpotlight';
import DashboardSkeleton from '../components/skeleton/DashboardSkeleton';
import { useVehicleType } from '../components/VehicleTypeSelector';

const DEBUG_TOOLS_ENABLED = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENABLE_DEBUG_TOOLS) === 'true';

/** Persists company-admin dashboard Joyride dismissal so Skip/Finish survives navigation and API hiccups */
const COMPANY_ADMIN_TOUR_LS_KEY = 'hasSeenCompanyAdminDashboardTour';

// Helper to count items expiring soon
const countExpiringSoon = (items, dateField, daysThreshold = 30) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const thresholdDate = new Date(now);
    thresholdDate.setDate(now.getDate() + daysThreshold);

    return items.filter(item => {
        if (!item[dateField]) return false;
        try {
            const expiryDate = new Date(item[dateField]);
            expiryDate.setHours(0, 0, 0, 0);
            return expiryDate >= now && expiryDate <= thresholdDate;
        } catch (e) {
            return false;
        }
    }).length;
};

function Dashboard() {
    const [trucks, setTrucks] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user, updateUserProfile } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
    const [runTour, setRunTour] = useState(false);
    const [, setTourCompleted] = useState(false);
    const tourDoneRef = useRef(false);

    const tourSteps = [
        {
            target: '[data-tour-id="nav-customers"]',
            content: 'Add your first customer to start mapping locations and jobs.',
            disableBeacon: true
        },
        {
            target: '[data-tour-id="nav-truckbook"]',
            content: 'Truckbook is where you add flatbeds, vans, and trailers.',
        },
        {
            target: '[data-tour-id="nav-company-management"]',
            content: 'Company Management: review weekly checklists, approve time off and apparel orders.'
        }
    ];

    const getActivityLink = (item) => {
        if (!item) return '/dashboard';
        if (item.type === 'apparel') return '/forms-management';
        if (item.type === 'timeoff') return '/forms/time-off';
        if (item.type === 'checklist') return '/forms/weekly-checklist';
        return '/dashboard';
    };
    const { selectedType, isAll, isVans, isFlatbeds } = useVehicleType();
    const [debugResult, setDebugResult] = useState(null);
    
    // Get current date in formatted string
    const currentDate = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    // Get time-based greeting
    const getTimeBasedGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    // Get dashboard status message
    const getDashboardStatus = () => {
        if (loading) return 'Loading your operations overview...';
        
        const issues = trucksNeedingAttention;
        const totalOperations = trucks.length + customers.length + totalDropLots;
        
        if (issues === 0) {
            return `All systems running smoothly across ${totalOperations} operational units.`;
        } else if (issues === 1) {
            return `${issues} item needs attention across your ${totalOperations} operational units.`;
        } else {
            return `${issues} items need attention across your ${totalOperations} operational units.`;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch all relevant data for dashboard
                const [trucksRes, customersRes] = await Promise.all([
                    apiClient.get('/trucks').catch(e => { 
                        console.error("Truck fetch error:", e); 
                        return { data: { data: [] }, error: e }; 
                    }),
                    apiClient.get('/customers').catch(e => { 
                        console.error("Customers fetch error:", e); 
                        return { data: { data: [] }, error: e }; 
                    })
                ]);

                // Extract data from responses with fallbacks
                const fetchedTrucks = trucksRes?.data?.data || trucksRes?.data || [];
                const fetchedCustomers = customersRes?.data?.data || customersRes?.data || [];
                
                // Log the data to help with debugging
                console.log("[Dashboard] Customers data received:", 
                    Array.isArray(fetchedCustomers) ? 
                    `${fetchedCustomers.length} customers, first has coordinates: ${fetchedCustomers[0]?.address?.coordinates ? 'Yes' : 'No'}` : 
                    'Not an array');
                
                let fetchError = trucksRes?.error || customersRes?.error;

                if (fetchError) {
                    console.error("Dashboard fetch error occurred:", fetchError);
                    setError("Some dashboard data failed to load. Please try again later.");
                }

                setTrucks(fetchedTrucks);
                setCustomers(fetchedCustomers);

            } catch (err) {
                console.error("Dashboard Promise.all or processing error:", err);
                setError("Failed to load dashboard data. Please check connection or try again later.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Mirror server flag into localStorage so we stay in sync after profile updates
    useEffect(() => {
        if (user?.hasSeenCompanyAdminTour === true) {
            try {
                localStorage.setItem(COMPANY_ADMIN_TOUR_LS_KEY, '1');
            } catch (_) {}
        }
    }, [user?.hasSeenCompanyAdminTour]);

    const tourShouldRun = useMemo(() => {
        if (user?.role !== 'company_admin') return false;
        if (user?.hasSeenCompanyAdminTour === true) return false;
        try {
            if (localStorage.getItem(COMPANY_ADMIN_TOUR_LS_KEY) === '1') return false;
        } catch (_) {}
        return true;
    }, [user?.role, user?.hasSeenCompanyAdminTour]);

    // Start tour only when allowed (not dismissed in storage or DB)
    useEffect(() => {
        if (tourShouldRun) {
            setRunTour(true);
        } else {
            setRunTour(false);
        }
    }, [tourShouldRun]);

    const markCompanyAdminTourDone = useCallback(async () => {
        if (tourDoneRef.current) return;
        tourDoneRef.current = true;
        setTourCompleted(true);
        setRunTour(false);
        try {
            localStorage.setItem(COMPANY_ADMIN_TOUR_LS_KEY, '1');
        } catch (_) {}
        try {
            await updateUserProfile({ hasSeenCompanyAdminTour: true });
        } catch (_) {
            // localStorage already prevents repeat; profile can sync later
        }
    }, [updateUserProfile]);

    const handleTourCallback = async (data) => {
        const { status, type, action } = data;
        const finishedStatuses = ['finished', 'skipped'];
        // With continuous=true, the corner X calls close() which only advances to the next step.
        // Treat X (and Esc / overlay close, which use the same action) like Skip: end the tour and persist dismissal.
        if (action === 'close') {
            await markCompanyAdminTourDone();
            return;
        }
        if (finishedStatuses.includes(status)) {
            await markCompanyAdminTourDone();
            return;
        }
        if (type === 'tour:end' && finishedStatuses.includes(status)) {
            await markCompanyAdminTourDone();
        }
    };

    // Filter trucks based on selected vehicle type
    const filteredTrucks = trucks.filter(truck => {
        if (isAll) return true;
        if (isVans) return truck.type?.toLowerCase() === 'van' || truck.type?.toLowerCase() === 'vans';
        if (isFlatbeds) return truck.type?.toLowerCase() === 'flatbed' || truck.type?.toLowerCase() === 'flatbeds';
        return true;
    });

    // --- Enhanced Calculated Stats ---
    const truckStatusCounts = filteredTrucks.reduce((acc, truck) => {
        const status = truck?.status || 'Unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});

    // Calculate basic stats
    const totalTrucks = filteredTrucks.length;

    // Count customers with drop lot locations
    const customersWithDropLots = customers.filter(customer => 
        customer.dropLotLocations && customer.dropLotLocations.length > 0
    ).length;

    // Count total drop lot locations across all customers
    const totalDropLots = customers.reduce((total, customer) => 
        total + (customer.dropLotLocations?.length || 0), 0
    );

    // Calculate trucks needing attention (maintenance or inactive)
    const trucksNeedingAttention = (truckStatusCounts['Maintenance'] || 0) + 
                                  (truckStatusCounts['Inactive'] || 0) + 
                                  (truckStatusCounts['Sold'] || 0);

    // Recent activity (placeholder - in real app this would be actual recent data)
    const [recentFormActivity, setRecentFormActivity] = React.useState([]);
    const recentActivityCount = recentFormActivity.length;

    React.useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const resp = await apiClient.get('/forms/recent-activity');
                if (isMounted && resp?.data?.success) {
                    setRecentFormActivity(resp.data.data || []);
                }
            } catch (e) {
                console.warn('[Dashboard] Failed to load recent form activity', e);
            }
        })();
        return () => { isMounted = false; };
    }, []);

    // Admin/company_admin: My Tasks counts
    const [taskCounts, setTaskCounts] = useState({
        apparelSubmitted: 0,
        checklistsPendingReview: 0,
        timeoffPending: 0
    });

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!(user?.role === 'admin' || user?.role === 'company_admin')) return;
            try {
                const resp = await apiClient.get('/forms/stats');
                const data = resp?.data?.data || {};
                const apparelSubmitted = (data?.apparel?.submitted ?? data?.apparel?.pending ?? data?.apparelSubmitted ?? 0);
                const checklistsPendingReview = (data?.weeklyChecklists?.pendingReview ?? data?.weekly?.pendingReview ?? data?.checklistsPending ?? 0);
                const timeoffPending = (data?.timeOff?.pending ?? data?.timeoff?.pending ?? data?.timeOffPending ?? 0);
                if (mounted) setTaskCounts({ apparelSubmitted, checklistsPendingReview, timeoffPending });
            } catch (e) {
                // Fallback to fetching full lists and counting client-side
                try {
                    const [apparelRes, checklistRes, timeoffRes] = await Promise.all([
                        apiClient.get('/forms/apparel'),
                        apiClient.get('/forms/weekly-checklist'),
                        apiClient.get('/forms/time-off')
                    ]);
                    if (!mounted) return;
                    const apparel = apparelRes?.data?.data || apparelRes?.data || [];
                    const checklists = checklistRes?.data?.data || checklistRes?.data || [];
                    const timeoffs = timeoffRes?.data?.data || timeoffRes?.data || [];
                    setTaskCounts({
                        apparelSubmitted: apparel.filter(o => (o.status || '').toLowerCase() === 'submitted').length,
                        checklistsPendingReview: checklists.filter(c => !c.reviewedBy).length,
                        timeoffPending: timeoffs.filter(r => (r.status || '').toLowerCase() === 'pending').length
                    });
                } catch (e2) {
                    console.warn('[Dashboard] Failed to load task counts', e2);
                }
            }
        };
        load();
        return () => { mounted = false; };
    }, [user?.role]);

    const validChartData = Object.entries(truckStatusCounts)
                               .filter(([name]) => name !== 'Unknown')
                               .map(([name, value]) => ({ name, value }));

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Active': return <NoCrash color="success" fontSize="small" />;
            case 'Maintenance': return <Build color="warning" fontSize="small" />;
            case 'Inactive':
            case 'Sold': return <Cancel color="error" fontSize="small" />;
            default: return <LocalShipping color="disabled" fontSize="small" />;
        }
    };

    // Render Quick Actions card content so we can reuse it in multiple places
    const renderQuickActionsCardContent = () => (
        <Card 
            sx={{ 
                background: 'linear-gradient(135deg, #B71C1C 0%, #D32F2F 100%)',
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(183, 28, 28, 0.2)',
                height: { xs: 'auto', md: 140 },
                '& *': {
                    color: 'white !important'
                }
            }}
        >
            <CardContent sx={{ 
                p: { xs: 2.5, md: 2 }, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center',
                height: '100%'
            }}>
                <Stack direction="row" alignItems="center" mb={1.25}>
                    <TrendingUp sx={{ fontSize: isMobile ? 28 : 24, mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: isMobile ? '1.05rem' : undefined }}>
                        Quick Actions
                    </Typography>
                </Stack>
                <Stack spacing={isMobile ? 1.25 : 0.5}>
                    <Button 
                        size={isMobile ? 'large' : 'small'}
                        fullWidth={isMobile}
                        component={RouterLink} 
                        to={{ pathname: '/forms/weekly-checklist', search: isMobile ? '?quick=1' : undefined, state: { quickOpen: true } }}
                        sx={{ 
                            bgcolor: 'rgba(255,255,255,0.2)', 
                            color: 'white',
                            fontSize: isMobile ? '0.95rem' : '0.75rem',
                            py: isMobile ? 1.1 : 0.5,
                            borderRadius: isMobile ? 2 : 1,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                    >
                        Weekly Checklist
                    </Button>
                    <Button 
                        size={isMobile ? 'large' : 'small'}
                        fullWidth={isMobile}
                        component={RouterLink} 
                        to={{ pathname: '/forms/time-off', search: isMobile ? '?quick=1' : undefined, state: { quickOpen: true } }}
                        sx={{ 
                            bgcolor: 'rgba(255,255,255,0.2)', 
                            color: 'white',
                            fontSize: isMobile ? '0.95rem' : '0.75rem',
                            py: isMobile ? 1.1 : 0.5,
                            borderRadius: isMobile ? 2 : 1,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                    >
                        Time Off Request
                    </Button>
                    <Button 
                        size={isMobile ? 'large' : 'small'}
                        fullWidth={isMobile}
                        component={RouterLink} 
                        to={{ pathname: '/forms/apparel', search: isMobile ? '?quick=1' : undefined, state: { quickOpen: true } }}
                        sx={{ 
                            bgcolor: 'rgba(255,255,255,0.2)', 
                            color: 'white',
                            fontSize: isMobile ? '0.95rem' : '0.75rem',
                            py: isMobile ? 1.1 : 0.5,
                            borderRadius: isMobile ? 2 : 1,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                    >
                        Apparel Order
                    </Button>
                    {/* Debug button gated by env flag */}
                    {DEBUG_TOOLS_ENABLED && (
                      <Button 
                          size={isMobile ? 'large' : 'small'}
                          fullWidth={isMobile}
                          variant="contained"
                          color="warning"
                          onClick={async () => {
                              try {
                                  const res = await apiClient.get('/intake/debug-auth');
                                  setDebugResult({ status: res.status, data: res.data });
                              } catch (e) {
                                  setDebugResult({ error: e?.response?.data || e.message });
                              }
                          }}
                      >
                          Test Intake (Debug JSON)
                      </Button>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );

    // Get the nearest 3 customers (placeholder for actual proximity calculation)
    const getNearestCustomers = () => {
        // In a real app, this would use geolocation to sort by actual distance
        return customers.slice(0, 3);
    };
    
    // Handle map marker clicks
    const handleMarkerClick = (customer) => {
        console.log("Customer marker clicked:", customer.name);
        // Could navigate to customer detail page or show more info
    };

    // Show skeleton while loading
    if (loading) {
        return (
            <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: { xs: 0.5, md: 1 } }}>
                <DashboardSkeleton />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: { xs: 0.5, md: 1 } }}>
            {/* Company Admin Onboarding Tour */}
            {user?.role === 'company_admin' && (
                <Joyride
                    steps={tourSteps}
                    run={runTour}
                    continuous
                    showSkipButton
                    spotlightClicks
                    disableScrolling={false}
                    styles={{
                        options: {
                            primaryColor: '#E94F37',
                            zIndex: 2000
                        }
                    }}
                    callback={handleTourCallback}
                />
            )}
            {/* Quick Actions - show at very top on mobile */}
            {isMobile && (
                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                    {renderQuickActionsCardContent()}
                </Box>
            )}

            {/* Top Row: Driver Spotlight (left) and Customer Map (right) */}
            <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: { xs: 3, md: 4 } }}>
                <Grid item xs={12} md={6}>
                    <Paper 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid rgba(0,0,0,0.05)',
                            height: { xs: 300, md: 400 },
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                mb: { xs: 2, md: 3 }, 
                                fontWeight: 600,
                                color: 'text.primary'
                            }}
                        >
                            Driver Spotlight
                        </Typography>
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            <DriverSpotlight />
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid rgba(0,0,0,0.05)'
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                mb: { xs: 2, md: 3 }, 
                                fontWeight: 600,
                                color: 'text.primary'
                            }}
                        >
                            Customer Locations Map
                        </Typography>
                        <Box sx={{ height: { xs: 300, md: 400 }, borderRadius: 2, overflow: 'hidden' }}>
                            <MapComponent 
                                customers={customers} 
                                onMarkerClick={handleMarkerClick}
                                height="100%"
                            />
            </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Vehicle Type Filter Indicator */}
            {!isAll && (
                <Box sx={{ mb: 2 }}>
                    <Chip
                        label={`Showing ${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} Only`}
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ 
                            fontWeight: 500,
                            borderColor: '#E94F37',
                            color: '#E94F37'
                        }}
                    />
                </Box>
            )}
            
            {error && (
                <Alert 
                    severity="error" 
                    sx={{ 
                        mb: { xs: 2, md: 3 },
                        borderRadius: 2
                    }}
                >
                    {error}
                </Alert>
            )}

            {/* Navigation Quick Access Cards */}
            <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: 1.5 }}>
                {/* Quick Actions - hide in grid on mobile to avoid duplication */}
                {!isMobile && (
                    <Grid item xs={12} sm={6} lg={2.4}>
                        {renderQuickActionsCardContent()}
                    </Grid>
                )}
                
                {/* Customers */}
                <Grid item xs={12} sm={6} lg={2.4}>
                    <Card 
                        onClick={() => navigate('/customers')}
                        sx={{ 
                            background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                            color: 'white',
                            cursor: 'pointer',
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(21, 101, 192, 0.2)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            height: 140,
                            '&:hover': { 
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 32px rgba(21, 101, 192, 0.3)'
                            },
                            '& *': {
                                color: 'white !important'
                            }
                        }}
                    >
                        <CardContent sx={{ 
                            p: 2, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '100%',
                            textAlign: 'center'
                        }}>
                            <Group sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Customers
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {customers.length} total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                {/* Truckbook */}
                <Grid item xs={12} sm={6} lg={2.4}>
                    <Card 
                        onClick={() => navigate('/trucks')}
                        sx={{ 
                            background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
                            color: 'white',
                            cursor: 'pointer',
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(27, 94, 32, 0.2)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            height: 140,
                            '&:hover': { 
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 32px rgba(27, 94, 32, 0.3)'
                            },
                            '& *': {
                                color: 'white !important'
                            }
                        }}
                    >
                        <CardContent sx={{ 
                            p: 2, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '100%',
                            textAlign: 'center'
                        }}>
                            <LocalShipping sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Truckbook
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {totalTrucks} vehicles
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Safety */}
                <Grid item xs={12} sm={6} lg={2.4}>
                    <Card 
                        onClick={() => navigate('/safety')}
                        sx={{ 
                            background: 'linear-gradient(135deg, #E65100 0%, #FF9800 100%)',
                            color: 'white',
                            cursor: 'pointer',
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(230, 81, 0, 0.2)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            height: 140,
                            '&:hover': { 
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 32px rgba(230, 81, 0, 0.3)'
                            },
                            '& *': {
                                color: 'white !important'
                            }
                        }}
                    >
                        <CardContent sx={{ 
                            p: 2, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '100%',
                            textAlign: 'center'
                        }}>
                            <Security sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Safety
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Center
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Forms Center */}
                <Grid item xs={12} sm={6} lg={2.4}>
                    <Card 
                        onClick={() => navigate('/forms')}
                        sx={{ 
                            background: 'linear-gradient(135deg, #4A148C 0%, #7B1FA2 100%)',
                            color: 'white',
                            cursor: 'pointer',
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(74, 20, 140, 0.2)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            height: 140,
                            '&:hover': { 
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 32px rgba(74, 20, 140, 0.3)'
                            },
                            '& *': {
                                color: 'white !important'
                            }
                        }}
                    >
                        <CardContent sx={{ 
                            p: 2, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '100%',
                            textAlign: 'center'
                        }}>
                            <Assignment sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Forms
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Center
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>


            </Grid>

            {/* Main Content: Admin tasks (if applicable) + Recent Activity full width */}
            <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {(user?.role === 'admin' || user?.role === 'company_admin') && (
                <Grid item xs={12}>
                    <Paper 
                        sx={{ 
                            p: 1.5, 
                            mb: 1.5, 
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid rgba(0,0,0,0.05)'
                        }}
                    >
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>My Tasks</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                <Button size="small" variant="outlined" onClick={() => navigate('/forms-management?tab=1')}>
                                    {taskCounts.apparelSubmitted} apparel orders need status update
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => navigate('/forms-management?tab=2')}>
                                    {taskCounts.checklistsPendingReview} checklists awaiting review
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => navigate('/forms-management?tab=0')}>
                                    {taskCounts.timeoffPending} time-off requests pending
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
            )}
            <Grid item xs={12}>
                {/* Recent Activity (moved to top priority) */}
                <Paper 
                        sx={{ 
                            p: 1.5, 
                            mb: 1.5, 
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid rgba(0,0,0,0.05)'
                        }}
                    >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }} component="div">
                            Recent Activity
                        </Typography>
                        <Chip 
                            label={recentActivityCount} 
                            size="small" 
                            color="primary"
                            sx={{ fontWeight: 600 }}
                        />
                    </Stack>
                    <List sx={{ p: 0 }}>
                        {recentFormActivity.slice(0, 5).map((item, index) => (
                            <React.Fragment key={`${item.type}-${item.id}`}>
                                <ListItem 
                                    component={RouterLink}
                                    to={getActivityLink(item)}
                                    sx={{ px: 0, py: 1.25, borderRadius: 2, '&:hover': { bgcolor: 'action.hover' }, textDecoration: 'none' }}
                                >
                                    <ListItemIcon>
                                        {item.type === 'apparel' ? <Assignment /> : item.type === 'timeoff' ? <Event /> : <CheckCircle />}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" fontWeight={600} color="text.primary">
                                                {item.title}
                                            </Typography>
                                        }
                                        secondary={
                                            <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                                                <Chip 
                                                    label={item.type}
                                                    size="small"
                                                    color={item.type === 'apparel' ? 'secondary' : item.type === 'timeoff' ? 'warning' : 'success'}
                                                    sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.by} • {new Date(item.when).toLocaleString()}
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                </ListItem>
                                {index < 4 && <Divider />}
                            </React.Fragment>
                        ))}
                        {recentFormActivity.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <Typography color="text.secondary">No recent form activity</Typography>
                            </Box>
                        )}
                    </List>
                    {recentFormActivity.length > 5 && (
                        <Box sx={{ textAlign: 'right', mt: 1 }}>
                            <Button 
                                component={RouterLink}
                                to="/forms"
                                size="small"
                                endIcon={<NavigateNext />}
                            >
                                View more
                            </Button>
                        </Box>
                    )}
                    </Paper>

                {/* Removed right column; recent activity spans full width */}
                </Grid>
            </Grid>
            {debugResult && (
                <Paper sx={{ p: 2, mb: 2, mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Debug Result</Typography>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(debugResult, null, 2)}</pre>
                </Paper>
            )}
        </Container>
    );
}

export default Dashboard; 